import { Component } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FormlyFieldConfig, FormlyForm } from "@ngx-formly/core";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { FluidModule } from "primeng/fluid";
import { finalize } from "rxjs/operators";
import {
  TransactionActionType,
  TransactionApprovalDialogConfig,
  TransactionApprovalFormModel,
} from "./transaction-approval-dialog.types";
import { getTransactionActionFormFields } from "./transaction-approval-form-definition";
import { FormService } from "../../../../services/form.service";
import { ToastService } from "../../../../services/toast.service";
import { TransactionService } from "../../../../services/transaction.service";

import { primeNgPageModules } from "../../../../shared/modules/prime-ng-common.module";

@Component({
  templateUrl: "./transaction-approval.dialog.html",
  imports: [
    ...primeNgPageModules,
    ReactiveFormsModule,
    FormlyForm,
    DialogModule,
    ButtonModule,
    FluidModule,
    TranslocoDirective,
  ],
})
export class TransactionApprovalDialog {
  public form = new FormGroup({});
  public model: TransactionApprovalFormModel = { actionMessage: "" };
  public fields: FormlyFieldConfig[];
  public actionType: TransactionActionType;
  public transactionTitle: string;
  public loading = false;

  private transactionId: number;

  constructor(
    public ref: DynamicDialogRef,
    public formService: FormService,
    public config: DynamicDialogConfig<TransactionApprovalDialogConfig>,
    private transactionService: TransactionService,
    private translocoService: TranslocoService,
    private toastService: ToastService,
  ) {
    if (!this.config.data?.transactionId) {
      const error = new Error("Transaction ID not found");
      this.toastService.error(this.translocoService.translate("app.common.errors.unknown"));
      this.ref.close();
      throw error;
    }

    this.transactionId = this.config.data.transactionId;
    this.actionType = this.config.data.actionType;
    this.transactionTitle = this.config.data.transactionTitle!;

    this.fields = this.formService.prepareFormFields(getTransactionActionFormFields(this.actionType));
  }

  public submitAction(): void {
    this.loading = true;

    if (this.form.valid) {
      const message = (this.form.value as TransactionApprovalFormModel)["actionMessage"] || "";

      if (this.actionType === "approval") {
        this.approveTransaction(message);
      } else {
        this.rejectTransaction(message);
      }
    }
  }

  private approveTransaction(message: string): void {
    this.transactionService
      .addApprovalEntry$(this.transactionId, message, false)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          this.toastService.success(this.translocoService.translate("app.transactionDetails.approvalSuccess"));
          this.ref.close({ success: true, actionType: "approval", response });
        },
        error: () => {
          this.toastService.error(this.translocoService.translate("app.transactionDetails.approvalError"));
        },
      });
  }

  private rejectTransaction(message: string): void {
    this.transactionService
      .addRejectionEntry$(this.transactionId, message, false)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          this.toastService.success(this.translocoService.translate("app.transactionDetails.rejectionSuccess"));
          this.ref.close({ success: true, actionType: "rejection", response });
        },
        error: () => {
          this.toastService.error(this.translocoService.translate("app.transactionDetails.rejectionError"));
        },
      });
  }

  /**
   * t(app.transactionDetails.approvalDialog.approvalDescription)
   * t(app.transactionDetails.approvalDialog.rejectionDescription)
   */
  public getDescriptionText(): string {
    const key =
      this.actionType === "approval"
        ? "app.transactionDetails.approvalDialog.approvalDescription"
        : "app.transactionDetails.approvalDialog.rejectionDescription";
    return this.translocoService.translate(key);
  }

  /**
   * t(app.transactionDetails.approve)
   * t(app.transactionDetails.reject)
   */
  public getSubmitButtonText(): string {
    const key = this.actionType === "approval" ? "app.transactionDetails.approve" : "app.transactionDetails.reject";
    return this.translocoService.translate(key);
  }
}
